import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const audioInsights = [
  { icon: 'ri-speed-line', label: '语速变化', desc: '识别教师在不同教学环节的语速转换，判断讲授节奏是否与内容结构匹配' },
  { icon: 'ri-pause-circle-line', label: '停顿位置', desc: '核心概念前后是否留出停顿，帮助定位知识展开的关键节点' },
  { icon: 'ri-volume-up-line', label: '重音分布', desc: '关键结论和概念是否得到强调，评估表达推进的清晰程度' },
  { icon: 'ri-user-voice-line', label: '说话人分离', desc: '区分教师与学生发言，建立对话角色网络，辅助分析师生互动结构与问答分布' },
];

export default function P29AudioUnderstanding({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-200/30 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-200/25 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-8 md:py-10">
        <div ref={titleRef} className="text-center mb-6 md:mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100/60 text-primary-700 text-base font-body font-semibold">
              <i className="ri-scan-line text-sm" /> 技术点三
            </span>
          </div>
          <h2 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            课堂声音理解：<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">节奏、停顿与表达推进</span>
          </h2>
          <p className={`mt-3 text-lg md:text-xl text-foreground-950 font-body max-w-2xl mx-auto transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            通过声学特征分析，从语速、停顿、重音和句段转换四个维度，刻画课堂讲授的表达推进模式
          </p>
        </div>

        {/* 声音分析四个维度 — 大图卡片 */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 max-w-5xl mx-auto transition-all duration-1000 delay-400 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {audioInsights.map((item, i) => (
            <div
              key={item.label}
              className="group relative p-5 md:p-7 rounded-2xl bg-background-50/40 backdrop-blur-md border border-background-200/50 text-center transition-all duration-500 hover:shadow-lg hover:border-primary-300/40 hover:-translate-y-1 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06),inset_0_1px_1px_0_rgba(255,255,255,0.5)]"
              style={{ transitionDelay: `${400 + i * 100}ms` }}
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-primary-500 flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                <i className={`${item.icon} text-3xl text-background-50`} />
              </div>
              <h3 className="text-3xl font-body font-bold text-foreground-950 mb-2">{item.label}</h3>
              <p className="text-lg md:text-xl text-foreground-950 font-body leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}